import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const TransferJobs = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    status: "",
    role: "",
    page: 1,
    limit: 20,
  });
  const [pagination, setPagination] = useState({});
  const [error, setError] = useState("");
  const [retrying, setRetrying] = useState({});
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => {
            return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
          })
          .join(""),
      );

      const decoded = JSON.parse(jsonPayload);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin");
        return false;
      }
      return true;
    } catch (error) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    const token = localStorage.getItem("ogestyle_token");
    setLoading(true);
    setError("");

    try {
      const queryParams = new URLSearchParams({
        page: filters.page,
        limit: filters.limit,
      });
      if (filters.status) queryParams.append("status", filters.status);
      if (filters.role) queryParams.append("role", filters.role);

      const [jobsRes, statsRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/admin/transfer-jobs?${queryParams}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/api/admin/transfer-jobs/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setJobs(jobsRes.data.jobs || []);
      setPagination(jobsRes.data.pagination || {});
      setStats(statsRes.data.stats);
    } catch (error) {
      setError("Failed to load transfer jobs");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchData();
    }
  }, [checkPortalAdmin, fetchData]);

  const handleRetry = async (jobId) => {
    const token = localStorage.getItem("ogestyle_token");
    setRetrying({ ...retrying, [jobId]: true });
    setError("");

    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/admin/transfer-jobs/${jobId}/retry`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.data.success) {
        alert("✅ Transfer executed successfully!");
        fetchData(); // Refresh list
      } else {
        alert(
          `❌ Retry failed: ${response.data.message || response.data.error}`,
        );
      }
    } catch (error) {
      alert(
        `❌ Retry failed: ${error.response?.data?.message || error.message}`,
      );
    } finally {
      setRetrying({ ...retrying, [jobId]: false });
    }
  };

  const viewJobDetails = async (jobId) => {
    const token = localStorage.getItem("ogestyle_token");
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/transfer-jobs/${jobId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      setSelectedJob(response.data);
      setShowDetails(true);
    } catch (error) {
      alert("Failed to load job details");
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "#f59e0b", label: "Pending", icon: "⏳" },
      transfer_pending: { color: "#3b82f6", label: "Processing", icon: "🔄" },
      paid_out: { color: "#10b981", label: "Paid Out", icon: "✅" },
      failed: { color: "#ef4444", label: "Failed", icon: "❌" },
      owner_internal: { color: "#6b7280", label: "Internal", icon: "🏠" },
      canceled: { color: "#9ca3af", label: "Canceled", icon: "🚫" },
    };

    const config = statusConfig[status] || {
      color: "#6b7280",
      label: status,
      icon: "⚪",
    };
    return (
      <span
        style={{
          padding: "4px 12px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: "600",
          background: `${config.color}20`,
          color: config.color,
          border: `1px solid ${config.color}40`,
        }}
      >
        {config.icon} {config.label}
      </span>
    );
  };

  const getRoleLabel = (role) => {
    const roleMap = {
      R1: { label: "Business", color: "#1a659e", icon: "🏢" },
      R3: { label: "Professional", color: "#059669", icon: "💼" },
      R4: { label: "Freelancer", color: "#9333ea", icon: "🎨" },
      R7: { label: "Booth Renter", color: "#ea580c", icon: "🪑" },
    };
    const config = roleMap[role] || {
      label: role,
      color: "#6b7280",
      icon: "👤",
    };
    return (
      <span style={{ color: config.color, fontWeight: "600" }}>
        {config.icon} {config.label}
      </span>
    );
  };

  const hasBalanceIssues = stats?.balanceIssues > 0;

  return (
    <PortalAdminLayout>
      <div className="portal-dashboard">
        <div className="dashboard-header">
          <h1>Transfer Jobs</h1>
          <p>Canonical payout transfer job monitoring and management</p>
        </div>

        {/* Tab Navigation */}
        <div
          style={{ marginBottom: "24px", borderBottom: "2px solid #e5e7eb" }}
        >
          <div style={{ display: "flex", gap: "4px" }}>
            <button
              onClick={() => {
                /* already on this page */
              }}
              style={{
                padding: "12px 24px",
                border: "none",
                background: "white",
                borderBottom: "3px solid #3b82f6",
                color: "#3b82f6",
                fontWeight: "600",
                cursor: "pointer",
                fontSize: "14px",
                position: "relative",
                top: "2px",
              }}
            >
              Transfer Jobs
            </button>
          </div>
        </div>

        {hasBalanceIssues && (
          <div
            style={{
              background: "#fef2f2",
              border: "1px solid #fecaca",
              borderLeft: "4px solid #ef4444",
              borderRadius: "8px",
              padding: "16px",
              marginBottom: "24px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <span style={{ fontSize: "24px" }}>⚠️</span>
              <div>
                <h3
                  style={{
                    margin: "0 0 4px 0",
                    color: "#991b1b",
                    fontSize: "16px",
                  }}
                >
                  {stats.balanceIssues} job(s) failed due to insufficient Stripe
                  balance
                </h3>
                <p style={{ margin: 0, color: "#7f1d1d", fontSize: "14px" }}>
                  Add funds to your Stripe account or retry failed transfers
                  after resolving balance issues.
                </p>
                <a
                  href="https://dashboard.stripe.com/test/balance"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: "inline-block",
                    marginTop: "8px",
                    padding: "6px 12px",
                    background: "#0570de",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "6px",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  View Stripe Balance →
                </a>
              </div>
            </div>
          </div>
        )}

        {stats && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            {stats.byStatus.map((stat) => (
              <div
                key={stat._id}
                style={{
                  background: "white",
                  padding: "16px",
                  borderRadius: "8px",
                  border: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    color: "#6b7280",
                    marginBottom: "4px",
                  }}
                >
                  {stat._id.toUpperCase()}
                </div>
                <div
                  style={{
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#111827",
                  }}
                >
                  {stat.count}
                </div>
                <div
                  style={{
                    fontSize: "14px",
                    color: "#6b7280",
                    marginTop: "4px",
                  }}
                >
                  ${stat.totalAmount.toFixed(2)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            background: "white",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
            marginBottom: "24px",
          }}
        >
          <div style={{ padding: "16px", borderBottom: "1px solid #e5e7eb" }}>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value, page: 1 })
                }
                style={{
                  padding: "8px 12px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                }}
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="transfer_pending">Processing</option>
                <option value="paid_out">Paid Out</option>
                <option value="failed">Failed</option>
                <option value="owner_internal">Internal</option>
              </select>

              <select
                value={filters.role}
                onChange={(e) =>
                  setFilters({ ...filters, role: e.target.value, page: 1 })
                }
                style={{
                  padding: "8px 12px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                }}
              >
                <option value="">All Roles</option>
                <option value="R1">R1 - Business</option>
                <option value="R3">R3 - Professional</option>
                <option value="R4">R4 - Freelancer</option>
                <option value="R7">R7 - Booth Renter</option>
              </select>

              <button
                onClick={() => fetchData()}
                style={{
                  padding: "8px 16px",
                  background: "#3b82f6",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                }}
              >
                Refresh
              </button>
            </div>
          </div>

          {loading ? (
            <div
              style={{ padding: "48px", textAlign: "center", color: "#6b7280" }}
            >
              Loading transfer jobs...
            </div>
          ) : error ? (
            <div
              style={{ padding: "48px", textAlign: "center", color: "#ef4444" }}
            >
              {error}
            </div>
          ) : jobs.length === 0 ? (
            <div
              style={{ padding: "48px", textAlign: "center", color: "#6b7280" }}
            >
              No transfer jobs found
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr
                    style={{
                      background: "#f9fafb",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                      }}
                    >
                      Status
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                      }}
                    >
                      Role
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "right",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                      }}
                    >
                      Amount
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                      }}
                    >
                      Entries
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                      }}
                    >
                      Attempts
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "left",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                      }}
                    >
                      Created
                    </th>
                    <th
                      style={{
                        padding: "12px",
                        textAlign: "center",
                        fontSize: "12px",
                        fontWeight: "600",
                        color: "#6b7280",
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr
                      key={job._id}
                      style={{ borderBottom: "1px solid #e5e7eb" }}
                    >
                      <td style={{ padding: "12px" }}>
                        {getStatusBadge(job.status)}
                      </td>
                      <td style={{ padding: "12px" }}>
                        {getRoleLabel(job.beneficiary.role)}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "right",
                          fontWeight: "600",
                        }}
                      >
                        ${job.totalNetAmount.toFixed(2)}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "#6b7280",
                        }}
                      >
                        {job.ledgerEntryCount}
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: job.attemptCount >= 5 ? "#ef4444" : "#6b7280",
                        }}
                      >
                        {job.attemptCount}/5
                      </td>
                      <td
                        style={{
                          padding: "12px",
                          fontSize: "13px",
                          color: "#6b7280",
                        }}
                      >
                        {new Date(job.createdAt).toLocaleString()}
                      </td>
                      <td style={{ padding: "12px", textAlign: "center" }}>
                        <button
                          onClick={() => viewJobDetails(job._id)}
                          style={{
                            padding: "4px 12px",
                            marginRight: "8px",
                            background: "#f3f4f6",
                            border: "1px solid #d1d5db",
                            borderRadius: "4px",
                            cursor: "pointer",
                            fontSize: "12px",
                          }}
                        >
                          View
                        </button>
                        {(job.status === "failed" ||
                          job.status === "pending") && (
                          <button
                            onClick={() => handleRetry(job._id)}
                            disabled={retrying[job._id]}
                            style={{
                              padding: "4px 12px",
                              background: retrying[job._id]
                                ? "#9ca3af"
                                : "#10b981",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              cursor: retrying[job._id]
                                ? "not-allowed"
                                : "pointer",
                              fontSize: "12px",
                              fontWeight: "600",
                            }}
                          >
                            {retrying[job._id] ? "..." : "Retry"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pagination.pages > 1 && (
            <div
              style={{
                padding: "16px",
                borderTop: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <button
                onClick={() =>
                  setFilters({ ...filters, page: filters.page - 1 })
                }
                disabled={filters.page === 1}
                style={{
                  padding: "6px 12px",
                  background: filters.page === 1 ? "#f3f4f6" : "#3b82f6",
                  color: filters.page === 1 ? "#9ca3af" : "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: filters.page === 1 ? "not-allowed" : "pointer",
                }}
              >
                Previous
              </button>
              <span style={{ padding: "6px 12px", color: "#6b7280" }}>
                Page {pagination.page} of {pagination.pages}
              </span>
              <button
                onClick={() =>
                  setFilters({ ...filters, page: filters.page + 1 })
                }
                disabled={filters.page === pagination.pages}
                style={{
                  padding: "6px 12px",
                  background:
                    filters.page === pagination.pages ? "#f3f4f6" : "#3b82f6",
                  color:
                    filters.page === pagination.pages ? "#9ca3af" : "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor:
                    filters.page === pagination.pages
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {showDetails && selectedJob && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "rgba(0, 0, 0, 0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={() => setShowDetails(false)}
          >
            <div
              style={{
                background: "white",
                borderRadius: "8px",
                maxWidth: "800px",
                maxHeight: "80vh",
                overflow: "auto",
                padding: "24px",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <h2 style={{ margin: 0 }}>Job Details</h2>
                <button
                  onClick={() => setShowDetails(false)}
                  style={{
                    background: "none",
                    border: "none",
                    fontSize: "24px",
                    cursor: "pointer",
                  }}
                >
                  ×
                </button>
              </div>

              <div style={{ marginBottom: "24px" }}>
                <h3>Job Information</h3>
                <div
                  style={{
                    background: "#f9fafb",
                    padding: "16px",
                    borderRadius: "6px",
                  }}
                >
                  <div>
                    <strong>Job ID:</strong> {selectedJob.job._id}
                  </div>
                  <div>
                    <strong>Status:</strong>{" "}
                    {getStatusBadge(selectedJob.job.status)}
                  </div>
                  <div>
                    <strong>Role:</strong>{" "}
                    {getRoleLabel(selectedJob.job.beneficiary.role)}
                  </div>
                  <div>
                    <strong>Amount:</strong> $
                    {selectedJob.job.totalNetAmount.toFixed(2)}
                  </div>
                  <div>
                    <strong>Ledger Entries:</strong>{" "}
                    {selectedJob.job.ledgerEntryCount}
                  </div>
                  <div>
                    <strong>Attempts:</strong> {selectedJob.job.attemptCount}/5
                  </div>
                  {selectedJob.job.stripeTransferId && (
                    <div>
                      <strong>Stripe Transfer ID:</strong>{" "}
                      {selectedJob.job.stripeTransferId}
                    </div>
                  )}
                  {selectedJob.job.failureReason && (
                    <div style={{ color: "#ef4444" }}>
                      <strong>Failure Reason:</strong>{" "}
                      {selectedJob.job.failureReason}
                    </div>
                  )}
                </div>
              </div>

              {selectedJob.job.attempts &&
                selectedJob.job.attempts.length > 0 && (
                  <div style={{ marginBottom: "24px" }}>
                    <h3>Attempt History</h3>
                    <div
                      style={{
                        background: "#f9fafb",
                        padding: "16px",
                        borderRadius: "6px",
                      }}
                    >
                      {selectedJob.job.attempts.map((attempt, idx) => (
                        <div
                          key={idx}
                          style={{
                            marginBottom: "12px",
                            paddingBottom: "12px",
                            borderBottom:
                              idx < selectedJob.job.attempts.length - 1
                                ? "1px solid #e5e7eb"
                                : "none",
                          }}
                        >
                          <div>
                            <strong>Attempt {idx + 1}:</strong>{" "}
                            {new Date(attempt.attemptedAt).toLocaleString()}
                          </div>
                          <div>
                            <strong>Outcome:</strong> {attempt.outcome}
                          </div>
                          {attempt.errorCode && (
                            <div>
                              <strong>Error Code:</strong> {attempt.errorCode}
                            </div>
                          )}
                          {attempt.errorMessage && (
                            <div
                              style={{
                                color: "#ef4444",
                                fontSize: "13px",
                                marginTop: "4px",
                              }}
                            >
                              {attempt.errorMessage}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              {selectedJob.ledgerEntries &&
                selectedJob.ledgerEntries.length > 0 && (
                  <div>
                    <h3>Ledger Entries ({selectedJob.ledgerEntries.length})</h3>
                    <div style={{ maxHeight: "200px", overflow: "auto" }}>
                      {selectedJob.ledgerEntries.map((entry) => (
                        <div
                          key={entry._id}
                          style={{
                            background: "#f9fafb",
                            padding: "12px",
                            marginBottom: "8px",
                            borderRadius: "6px",
                            fontSize: "13px",
                          }}
                        >
                          <div>
                            <strong>ID:</strong> {entry._id}
                          </div>
                          <div>
                            <strong>Source:</strong> {entry.sourceType}
                          </div>
                          <div>
                            <strong>Net Amount:</strong> $
                            {entry.fees.netEligibleAmount.toFixed(2)}
                          </div>
                          <div>
                            <strong>Status:</strong> {entry.status}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>
    </PortalAdminLayout>
  );
};

export default TransferJobs;
